import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Overline, Spinner } from "@/components/Widgets";
import { Check, Crown, ShieldStar, Lightning } from "@phosphor-icons/react";
import { PageGuide } from "@/components/PageGuide";

const ICONS = { starter_monthly: Lightning, pro_monthly: Crown, agency_monthly: ShieldStar };

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  const [canceling, setCanceling] = useState(false);

  const refresh = () => {
    api.get("/subscription").then(({ data }) => setSub(data)).catch(() => {});
    api.get("/usage").then(({ data }) => setUsage(data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
    refresh();
  }, []);

  const subscribe = async (lookup_key) => {
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoadingKey(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll drop to the free tier limits.")) return;
    setCanceling(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription canceled");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCanceling(false);
    }
  };

  const isAdmin = sub?.is_admin;

  return (
    <div className="space-y-8 fade-up">
      <PageGuide title="How billing & plans work" steps={[
        "Review the three plans and pick the one that fits your growth stage.",
        "Click Subscribe to check out securely with Stripe (test card 4242 4242 4242 4242).",
        "Track your monthly scan and image usage in the usage meters.",
        "Cancel anytime — you'll drop to the free tier limits.",
      ]} />
      {isAdmin && (
        <Card testid="admin-unlimited-banner" className="tracing-border p-[1px]">
          <div className="bg-card p-5 flex items-center gap-3">
            <ShieldStar size={26} weight="fill" className="text-primary" />
            <div>
              <div className="font-head font-bold text-lg">Admin · Unlimited Access</div>
              <div className="text-sm text-muted-foreground">Your account has full access to every SMI service with no plan limits.</div>
            </div>
          </div>
        </Card>
      )}

      {sub?.status === "active" && sub?.plan && !isAdmin && (
        <Card testid="current-plan-banner" className="border-primary/40">
          <div className="flex items-center justify-between">
            <div>
              <Overline className="text-primary">CURRENT PLAN</Overline>
              <div className="font-head font-bold text-xl mt-1">{sub.plan.name}</div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-emerald-400 font-mono text-sm flex items-center gap-1"><Check weight="bold" /> Active</span>
              <button
                onClick={cancel}
                disabled={canceling}
                data-testid="cancel-subscription-btn"
                className="border border-border text-muted-foreground px-3 py-2 text-sm hover:text-accent hover:border-accent/40 transition-colors duration-200 disabled:opacity-50"
              >
                {canceling ? "Canceling…" : "Cancel plan"}
              </button>
            </div>
          </div>
        </Card>
      )}

      {usage && !isAdmin && (
        <Card testid="usage-card">
          <Overline className="mb-4">THIS MONTH'S USAGE · {usage.period}</Overline>
          <div className="grid sm:grid-cols-2 gap-6">
            <UsageBar label="Reputation scans" data={usage.scans} />
            <UsageBar label="AI brand images" data={usage.images} />
          </div>
        </Card>
      )}

      <div>
        <h2 className="font-head font-bold text-2xl tracking-tight">Choose your plan</h2>
        <p className="text-muted-foreground text-sm mt-1">Scale your reputation ops. Cancel anytime. Test card: 4242 4242 4242 4242.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {plans.map((p) => {
          const Icon = ICONS[p.id] || Lightning;
          const active = sub?.plan?.id === p.id && sub?.status === "active";
          return (
            <div key={p.id} data-testid={`plan-${p.id}`}
              className={`relative bg-card border p-6 flex flex-col ${p.popular ? "border-primary" : "border-border"}`}>
              {p.popular && (
                <span className="absolute -top-3 left-6 bg-primary text-primary-foreground font-semibold text-[0.65rem] font-mono uppercase tracking-wider px-2 py-1">Most popular</span>
              )}
              <Icon size={28} weight="fill" className={p.popular ? "text-primary" : "text-muted-foreground"} />
              <h3 className="font-head font-bold text-xl mt-4">{p.name}</h3>
              <p className="text-sm text-muted-foreground mt-1 h-10">{p.tagline}</p>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-mono text-4xl font-bold">${p.price}</span>
                <span className="text-muted-foreground text-sm mb-1">/mo</span>
              </div>
              <ul className="mt-5 space-y-2 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <Check size={16} weight="bold" className="text-primary mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">{f}</span>
                  </li>
                ))}
              </ul>
              <button
                disabled={loadingKey === p.id || active || isAdmin}
                onClick={() => subscribe(p.id)}
                data-testid={`subscribe-${p.id}`}
                className={`mt-6 w-full py-3 text-sm font-semibold transition-colors duration-200 disabled:opacity-60 ${
                  p.popular ? "bg-primary text-primary-foreground font-semibold hover:bg-primary/90" : "border border-border hover:bg-secondary"
                }`}>
                {isAdmin ? "Included (Admin)" : active ? "Current plan" : loadingKey === p.id ? "Redirecting…" : "Subscribe"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UsageBar({ label, data }) {
  const pct = data.unlimited ? 100 : Math.min(100, Math.round((data.used / Math.max(data.limit, 1)) * 100));
  const near = !data.unlimited && data.used >= data.limit;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm">{label}</span>
        <span className="font-mono text-sm text-muted-foreground">
          {data.used}{data.unlimited ? "" : ` / ${data.limit}`}{data.unlimited ? " · unlimited" : ""}
        </span>
      </div>
      <div className="h-2 bg-secondary overflow-hidden">
        <div className={`h-full transition-all duration-500 ${near ? "bg-accent" : "bg-primary"}`}
          style={{ width: `${data.unlimited ? 12 : pct}%` }} />
      </div>
      {near && <div className="text-xs text-destructive mt-1">Limit reached — upgrade to keep going.</div>}
    </div>
  );
}
